import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MessageCircle, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { AppCard, AppCardContent, AppButton } from "../ui";
import apiClient from "../../utils/apiClient";
import { fetchMetaBusinessConfig } from "../../utils/metaBusinessConnect";
import "./whatsapp-dashboard.scss";

/**
 * /whatsapp/connect/callback
 *
 * Meta redirects here after the Embedded Signup OAuth flow.
 * Reads the `code` query param and sends it to the backend to exchange
 * for an access token + auto-discover WABA + phone.
 */
const WhatsAppConnectCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  /**
   * Per-step report from the connect flow. A connection almost always fails at
   * one identifiable stage — most often the webhook subscription — so showing
   * which one turns "connection failed" into something the user can act on.
   * Empty for the legacy direct-to-Meta path, which reports no steps.
   */
  const [steps, setSteps] = useState([]);
  /**
   * The discovery receipt — what this one grant reached. A Meta connect is not a
   * WhatsApp connect: the same click brings back Pages, Instagram accounts, lead
   * forms, ad accounts and pixels, and showing the tally here is what tells the
   * owner the connect did more than it appears to have done.
   */
  const [receipt, setReceipt] = useState(null);
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const code = searchParams.get("code");
    const error = searchParams.get("error");
    const errorReason = searchParams.get("error_reason");

    if (error || errorReason) {
      setState("error");
      setMessage(searchParams.get("error_description") || errorReason || "Authorization was denied.");
      return;
    }

    if (!code) {
      setState("error");
      setMessage("No authorization code received from Meta.");
      return;
    }

    const rawState = searchParams.get("state") || "";
    // state=platform means this is the SA/tenant-admin connect flow
    const isPlatform = rawState === "platform";

    (async () => {
      try {
        /**
         * Which grant is coming back.
         *
         * Two dialogs return to this one registered URL, and they redeem at
         * different endpoints: a Pages code sent to the WhatsApp callback fails
         * scope validation, and a WhatsApp code sent to the business callback
         * stores a credential that cannot see a Page. The mode is read from the
         * signed state rather than guessed — a bare `business` literal is only
         * the fallback for when no relay was needed and nothing was signed.
         */
        let mode = rawState === "business" ? "business" : "institute";
        if (!isPlatform && rawState.split(".").length === 3) {
          try {
            const { data: resolved } = await apiClient.post("/whatsapp/connect/resolve-state", { state: rawState });
            if (resolved?.mode) mode = resolved.mode;
          } catch { /* fall through as an institute connect */ }
        }
        const isBusiness = mode === "business";

        // Fetch canonical redirect URI from backend to match what was used in the OAuth URL
        const configEndpoint = isPlatform ? "/sa/whatsapp/connect/config" : "/whatsapp/connect/config";
        const callbackEndpoint = isPlatform
          ? "/sa/whatsapp/connect/callback"
          : isBusiness
            ? "/whatsapp/connect/business/callback"
            : "/whatsapp/connect/callback";
        /**
         * The redirect URI has to match, byte for byte, the one sent to Meta
         * when the dialog opened — a mismatch fails the code exchange with a
         * message about a URI nobody typed.
         *
         * The business grant asks the lead-capture / marketing endpoints for it,
         * not the WhatsApp one. `/whatsapp/connect/config` is gated on the
         * WhatsApp permission matrix, in which `admin` holds no `config` right,
         * so for an enquiries admin it 403s — and the origin fallback below is
         * exactly wrong whenever the relay is in play, which is the one case the
         * canonical redirect URI exists for.
         */
        let redirectUri;
        const businessCfg = isBusiness && !isPlatform
          ? await fetchMetaBusinessConfig({ from: "lead-sources" })
          : null;
        if (businessCfg?.redirectUri) {
          redirectUri = businessCfg.redirectUri;
        } else {
          try {
            const configRes = await apiClient.get(configEndpoint);
            redirectUri = configRes.data.redirectUri || `${window.location.origin}/whatsapp/connect/callback`;
          } catch {
            redirectUri = `${window.location.origin}/whatsapp/connect/callback`;
          }
        }
        /**
         * An Embedded Signup code has no redirect URI — it came from FB.login on
         * the canonical host, not from a redirect. Sending one fails the
         * exchange, so the flow is carried through rather than assumed.
         */
        const isEmbedded = searchParams.get("flow") === "embedded_signup";
        const { data } = await apiClient.post(
          callbackEndpoint,
          isEmbedded ? { code, flow: "embedded_signup" } : { code, redirectUri },
        );
        setState("success");
        setSteps(data.steps || []);
        if (data.assetCounts) {
          setReceipt({ counts: data.assetCounts, unavailable: data.provider?.unavailable || [] });
        }

        // The business grant reports its own outcome — Pages and scopes, not a
        // phone number — so it must not be described in WhatsApp's words.
        if (isBusiness) {
          let bmsg = data.message || "Facebook connected.";
          if (data.missingScopes?.length) {
            bmsg += ` Not granted: ${data.missingScopes.join(", ")}.`;
          }
          setMessage(bmsg);
          return;
        }

        let msg = data.whatsappConnected === false
          // Partial success is the normal case for a lead-first institute: the
          // grant is stored, the Pages and forms are connected, there is simply
          // no WhatsApp number in it. Saying "failed" would be wrong.
          ? `Meta connected. WhatsApp was not: ${data.whatsappError || "no WhatsApp Business Account was shared"}`
          : `Connected! Phone: ${data.phoneNumber || data.displayPhoneNumber || data.phoneNumberId}`;
        // Meta Lead Ads onboarding summary (legacy direct-to-Meta path only)
        if (data.pages && data.pages.connected > 0) {
          msg += ` · ${data.pages.subscribed}/${data.pages.connected} Facebook Page(s) connected for lead forms`;
        }
        // Connected, but inbound messages would be lost — worth saying plainly.
        if (data.whatsappConnected !== false && data.webhookSubscribed === false) {
          msg += ' · ⚠ Webhooks are not subscribed — incoming messages will not arrive.';
        }
        setMessage(msg);
      } catch (err) {
        setState("error");
        setSteps(err?.response?.data?.steps || []);
        setMessage(
          err?.response?.data?.message || "Failed to connect WhatsApp. Please try again."
        );
      }
    })();
  }, [searchParams]);

  /** Meta's taxonomy, in the words a customer uses. Singular / plural. */
  const ASSET_LABELS = {
    whatsapp_waba: ["WhatsApp Business Account", "WhatsApp Business Accounts"],
    whatsapp_phone_number: ["WhatsApp Number", "WhatsApp Numbers"],
    facebook_page: ["Facebook Page", "Facebook Pages"],
    instagram_account: ["Instagram Account", "Instagram Accounts"],
    lead_form: ["Lead Form", "Lead Forms"],
    ad_account: ["Ad Account", "Ad Accounts"],
    pixel: ["Pixel", "Pixels"],
  };

  /**
   * What the grant reached. Also names what it did *not* reach: an area Meta
   * refused is normal — an institute that granted WhatsApp but not ads legitimately
   * has no ad accounts — and naming it beats a short list the owner has to explain
   * to themselves.
   */
  const renderReceipt = () =>
    receipt && (
      <ul className="wa-connect-steps">
        {Object.entries(receipt.counts).map(([kind, count]) => {
          const [one, many] = ASSET_LABELS[kind] || [kind, kind];
          return (
            <li key={kind} className="wa-connect-steps__item wa-connect-steps__item--ok">
              <CheckCircle size={14} />
              <span className="wa-connect-steps__label">{count === 1 ? one : many}</span>
              <span className="wa-connect-steps__detail">{count}</span>
            </li>
          );
        })}
        {receipt.unavailable.map((area) => (
          <li key={area} className="wa-connect-steps__item">
            <XCircle size={14} />
            <span className="wa-connect-steps__label">{area.replace(/_/g, " ")}</span>
            <span className="wa-connect-steps__detail">not shared</span>
          </li>
        ))}
      </ul>
    );

  /** The connect flow's stage-by-stage result, when the backend reported one. */
  const renderSteps = () =>
    steps.length > 0 && (
      <ul className="wa-connect-steps">
        {steps.map((step) => (
          <li
            key={step.key}
            className={`wa-connect-steps__item wa-connect-steps__item--${step.ok ? "ok" : "failed"}`}
          >
            {step.ok ? <CheckCircle size={14} /> : <XCircle size={14} />}
            <span className="wa-connect-steps__label">{step.label}</span>
            {step.detail && <span className="wa-connect-steps__detail">{step.detail}</span>}
          </li>
        ))}
      </ul>
    );

  return (
    <div className="wa-callback-page">
      <AppCard>
        <AppCardContent>
          <div className="wa-connect-empty">
            {state === "loading" && (
              <>
                <div className="wa-connect-empty__icon wa-connect-empty__icon--loading">
                  <Loader2 size={40} className="wa-spin" />
                </div>
                <h3>Connecting WhatsApp…</h3>
                <p className="wa-text-muted">
                  Exchanging credentials with Meta. This only takes a moment.
                </p>
              </>
            )}

            {state === "success" && (
              <>
                <div className="wa-connect-empty__icon wa-connect-empty__icon--success">
                  <CheckCircle size={40} />
                </div>
                <h3>{receipt ? "Meta Connected!" : "WhatsApp Connected!"}</h3>
                <p className="wa-text-muted">{message}</p>
                {renderReceipt()}
                {renderSteps()}
                <AppButton onClick={() => navigate(searchParams.get("state") === "platform" ? "/sa/whatsapp" : "/whatsapp")}>
                  <MessageCircle size={16} /> {searchParams.get("state") === "platform" ? "Go to WhatsApp Config" : "Go to WhatsApp Dashboard"}
                </AppButton>
              </>
            )}

            {state === "error" && (
              <>
                <div className="wa-connect-empty__icon wa-connect-empty__icon--error">
                  <XCircle size={40} />
                </div>
                <h3>Connection Failed</h3>
                <p className="wa-text-muted">{message}</p>
                {renderSteps()}
                <div className="wa-inline-actions">
                  <AppButton onClick={() => navigate(searchParams.get("state") === "platform" ? "/sa/whatsapp" : "/whatsapp")}>
                    {searchParams.get("state") === "platform" ? "Back to Config" : "Back to Dashboard"}
                  </AppButton>
                  <AppButton variant="outline" onClick={() => window.close()}>
                    Close Window
                  </AppButton>
                </div>
              </>
            )}
          </div>
        </AppCardContent>
      </AppCard>
    </div>
  );
};

export default WhatsAppConnectCallback;
